// Marks (SPEC F6): saved loops. Delete is soft, with undo (DESIGN_SYSTEM §5).

import type { HistoryEntry, Mark, StyleId } from '../../data/types';
import { getDb } from '../schema';

export const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;

export type NewMark = Omit<Mark, 'id' | 'createdAt' | 'deletedAt'>;

const newId = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `m${Date.now()}${Math.random().toString(36).slice(2, 8)}`);

/** Saves a mark and notes it in the history, together. */
export async function createMark(input: NewMark, styleId: StyleId, now: number): Promise<Mark> {
  const db = getDb();
  const mark: Mark = { ...input, id: newId(), createdAt: now };
  await db.transaction('rw', db.marks, db.history, db.records, db.projects, async () => {
    await db.marks.put(mark);
    await linkMarkIds(mark.id, undefined, mark.projectId);
    const entry: HistoryEntry = { videoId: mark.videoId, at: now, styleId, outcome: 'marked' };
    await db.history.add(entry);
    const cur = (await db.records.get(mark.videoId)) ?? { videoId: mark.videoId };
    await db.records.put({ ...cur, heardAt: now });
  });
  return mark;
}

export async function updateMark(id: string, patch: Partial<NewMark>): Promise<void> {
  const db = getDb();
  await db.transaction('rw', db.marks, db.projects, async () => {
    const before = await db.marks.get(id);
    await db.marks.update(id, patch);
    if (before && 'projectId' in patch && patch.projectId !== before.projectId) await linkMarkIds(id, before.projectId, patch.projectId);
  });
}

/** Keeps Project.markIds in step with a mark's project (the marks table is the source of truth). */
async function linkMarkIds(markId: string, from: string | undefined, to: string | undefined): Promise<void> {
  const db = getDb();
  if (from) {
    const p = await db.projects.get(from);
    if (p) await db.projects.update(from, { markIds: p.markIds.filter((i) => i !== markId) });
  }
  if (to) {
    const p = await db.projects.get(to);
    if (p && !p.markIds.includes(markId)) await db.projects.update(to, { markIds: [...p.markIds, markId] });
  }
}

export async function softDeleteMark(id: string, now: number): Promise<void> {
  await getDb().marks.update(id, { deletedAt: now });
}

export async function restoreMark(id: string): Promise<void> {
  await getDb().marks.update(id, { deletedAt: undefined });
}

/** Newest first, without the deleted ones. */
export async function listMarks(): Promise<Mark[]> {
  const all = await getDb().marks.orderBy('createdAt').reverse().toArray();
  return all.filter((m) => m.deletedAt === undefined);
}

/** Deleted marks past the undo window are gone for good. */
export async function purgeExpiredMarks(now: number): Promise<number> {
  const db = getDb();
  const dead = (await db.marks.toArray()).filter((m) => m.deletedAt !== undefined && now - m.deletedAt > UNDO_WINDOW_MS);
  await db.marks.bulkDelete(dead.map((m) => m.id));
  return dead.length;
}

export async function getMark(id: string): Promise<Mark | undefined> {
  const m = await getDb().marks.get(id);
  return m && m.deletedAt === undefined ? m : undefined;
}
